'use client';

import {
  createPortableDialog,
  Dialog,
  DialogCloseButton,
  DialogContent,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';
import { OfferInterstitial } from '@wwe-portal/ui/features/interstitial';
import { PurchaseButton } from '@wwe-portal/ui/features/offer';
import { S3_IMAGES_URL } from '@wwe-portal/web/utils/config';
import {
  InterstitialOffer,
  UpsellOffer,
} from '@wwe-portal/web/services/client/interstitial';
import { useIsPendingPayment } from '@wwe-portal/web/hooks/payment/use-xsolla-payment';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { useGetPaymentContext } from '@wwe-portal/web/hooks/offer/use-get-payment-context';
import { PaymentContext } from '@wwe-portal/web/providers/stores/payment-store';
import { useEffect } from 'react';
import {
  sendInterstitialCloseButtonTap,
  sendInterstitialOfferButtonTap,
  sendUIInterstitialLoaded,
  sendUIUpsellLoaded,
  sendUpsellOfferButtonTap,
} from '@wwe-portal/web/libs/analytics/interstitial';

import {
  ModalPriority,
  usePriorityModal,
} from '@wwe-portal/web/providers/stores/priority-dialog-store';
import { useTrackInterstitialHistory } from './history';
import { parseRegionalOfferPrice } from '@wwe-portal/web/utils/offer/mapper';
import { useGetOfferById } from '@wwe-portal/web/providers/queries/offer';

export interface InterstitialOfferDialogV2Props {
  interstitialData: UpsellOffer | InterstitialOffer;
  offerId: string;
  dialogId: string;
  onStartPayment: (payload: {
    offerData: OfferData;
    context: PaymentContext;
  }) => void;
  onClose: () => void;
}

const useIntersitialModal = (id: string) => {
  const modal = usePortableDialog(id);
  return usePriorityModal({
    id: modal.id,
    priority: ModalPriority.INTERSTITIAL,
    open: modal.visible,
  });
};

export const InterstitialOfferDialog = createPortableDialog(
  ({
    interstitialData,
    onClose,
    offerId,
    onStartPayment,
    dialogId,
  }: InterstitialOfferDialogV2Props) => {
    const modal = useIntersitialModal(dialogId);
    const {
      data: offerData,
      isPurchasable,
      isLoading,
    } = useGetOfferById(offerId);

    useTrackInterstitialHistory(modal.visible, interstitialData.updateKey);
    const isUpsellOffer =
      'purchasedOfferId' in interstitialData &&
      interstitialData.purchasedOfferId;

    const analyticsContext = {
      ...interstitialData,
      offerId,
      suggestedOfferId: interstitialData.suggestedOfferId,
    };

    const handleClose = () => {
      if (offerData) {
        sendInterstitialCloseButtonTap(
          analyticsContext,
          offerData.offerItems ?? []
        );
      }
      onClose();
    };
    const isPending = useIsPendingPayment(offerData);
    const getPaymentContext = useGetPaymentContext();

    const handlePurchase = () => {
      if (!offerData) {
        return;
      }
      if (isUpsellOffer) {
        sendUpsellOfferButtonTap({
          ...analyticsContext,
          offerId: interstitialData.purchasedOfferId,
        });
      } else if (offerData) {
        sendInterstitialOfferButtonTap(
          analyticsContext,
          offerData.offerItems ?? []
        );
      }

      onStartPayment({
        offerData,
        context: getPaymentContext(offerData),
      });
    };

    // Analytics
    useEffect(() => {
      if (isUpsellOffer) {
        sendUIUpsellLoaded({
          ...analyticsContext,
          offerId: interstitialData.purchasedOfferId,
        });
      } else if (offerData) {
        sendUIInterstitialLoaded(analyticsContext, offerData.offerItems ?? []);
      }
    }, [offerData]);

    if (!modal.visible || isLoading || !isPurchasable) {
      return null;
    }

    return (
      <Dialog
        open
        onOpenChange={(currentOpen) => {
          if (!currentOpen) {
            handleClose();
          }
        }}
      >
        <DialogContent
          variant="dialog"
          style={{ maxWidth: 500 }}
          data-id="InterstitialOfferDialog"
        >
          <OfferInterstitial
            title={interstitialData.name}
            description={interstitialData.description}
            cover={
              interstitialData.cover
                ? `${S3_IMAGES_URL}/${interstitialData.cover}`
                : undefined
            }
            actionBtn={
              <PurchaseButton
                price={parseRegionalOfferPrice(offerData).price}
                loading={isPending}
                onClick={handlePurchase}
              />
            }
          />
          <DialogCloseButton onClose={handleClose} />
        </DialogContent>
      </Dialog>
    );
  }
);
